import React, { useState, useEffect, useCallback } from 'react';
import {
  LoadingState,
  PageContainer,
  PageContent,
  PageHeader,
  PageTitle,
  VimeoSection,
  InputGroup,
  Label,
  InputWrapper,
  Input,
  Select,
  SyncButton,
  SyncButtonText,
  ImageUpload,
  ImagePlaceholder,
  UploadedImage,
  UploadIcon,
  ImageSection,
  ImageGroup,
  ThumbnailUpload,
  BannerUpload,
  ImageDeleteButton,
  DeleteIcon,
  LecturerSelectionSection,
  LecturerSection,
  MaterialsSection,
  MaterialsWrapper,
  MaterialsDescription,
  FileUploadButton,
  FileUploadButtonText,
  FileList,
  FileListTitle,
  FileItem,
  FileInfo,
  FileName,
  FileUrl,
  FileLink,
  RemoveFileButton,
  ImageUploadSection,
  ImageUploadGroup,
  ImageUploadWrapper,
  ImageUploadDescription,
  ImagePreview,
  PreviewImage,
  UploadingText,
  RecommendedSection,
  RecommendedImageSection,
  RecommendedImageGroup,
  RecommendedImageUpload,
  RecommendedImage,
  RecommendedImagePlaceholder,
  RecommendedPlusContainer,
  RecommendedRemoveButton,
  RecommendedImageTitle,
  FaqSection,
  FaqList,
  FaqItem,
  FaqInputGroup,
  FaqQuestionWrapper,
  FaqAnswerWrapper,
  FaqButtons,
  FaqInput,
  FaqAddButton,
  FaqRemoveButton,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  LectureListGrid,
  LectureListItem,
  LectureCheckbox,
  LectureItemImageContainer,
  LectureItemThumbnail,
  LectureItemInfo,
  LectureItemTitle,
  LectureItemTags,
  Tag,
  ModalFooter,
  ConfirmButton,
  ButtonSection,
  ButtonGroup,
  TempSaveWrapper,
  TempSaveButton,
  TempSaveButtonText,
  SaveWrapper,
  SaveButton,
  SaveButtonText,
} from './LectureRegisterLayoutStyles';
import {
  getLectureCategories,
  getVimeoInfo,
  createLecture,
  updateLecture,
  updateLectureThumbnail,
  updateLectureBanner,
  deleteLectureThumbnail,
  deleteLectureBanner,
  getLectureList,
  uploadLectureFiles,
  getAdminLectureDetail,
  getLectureFiles,
  deleteLectureFile,
  deleteLecture,
  updateLectureIntroduction,
  updateLecturerIntroduction,
  updateLectureReview,
} from '../../../services/admin/classApi';
import { getLecturers } from '../../../services/admin/classApi';
import {
  LectureCategory,
  LectureUpdateRequest,
  LectureListItem as LectureListItemType,
  AdminLectureDetail,
} from '../../../types/admin';
import type { Lecturer } from '../../../types/admin';

// 타입 정의
interface ImageUploadProps {
  className?: string;
  onImageChange?: (file: File | null) => void;
  disabled?: boolean;
  initialImageUrl?: string | null;
}


// 플러스 아이콘 컴포넌트 (ClassManagementComponent.tsx와 동일)
const PlusIcon: React.FC = () => (
  <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15.9994 4.13721C16.6622 4.13721 17.1994 4.67447 17.1994 5.33721V14.8039H26.6661C27.3288 14.8039 27.8661 15.3411 27.8661 16.0039C27.8661 16.6666 27.3288 17.2039 26.6661 17.2039H17.1994V26.6705C17.1994 27.3333 16.6622 27.8705 15.9994 27.8705C15.3367 27.8705 14.7994 27.3333 14.7994 26.6705V17.2039H5.33281C4.67007 17.2039 4.13281 16.6666 4.13281 16.0039C4.13281 15.3411 4.67007 14.8039 5.33281 14.8039H14.7994V5.33721C14.7994 4.67447 15.3367 4.13721 15.9994 4.13721Z" fill="#448181"/>
  </svg>
);


// 이미지 업로드 컴포넌트
const ImageUploadComponent: React.FC<ImageUploadProps & { onDelete?: () => void; showDeleteButton?: boolean }> = ({
  className = "",
  onImageChange,
  disabled = false,
  initialImageUrl = null,
  onDelete,
  showDeleteButton = false
}) => {
  const [imageUrl, setImageUrl] = useState<string | null>(initialImageUrl);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // 초기 이미지 URL이 변경되면 상태 업데이트 (즉시 반영)
  React.useEffect(() => {
    /// console.log('ImageUpload URL 변경 감지:', initialImageUrl);
    if (initialImageUrl === null || initialImageUrl === undefined || initialImageUrl === '') {
      setImageUrl(null);
    } else {
      setImageUrl(initialImageUrl);
    }
  }, [initialImageUrl]);

  const handleImageClick = (e: React.MouseEvent) => {
    // 삭제 버튼 클릭 시에는 파일 선택 동작하지 않음
    if ((e.target as HTMLElement).closest('.imageDeleteButton')) {
      return;
    }
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      /// console.log('미리보기 URL 생성:', url);
      setImageUrl(url);
      onImageChange?.(file);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.();
  };

  return (
    <ImageUpload
      className={`${className} imageDeleteButton-root`}
      $disabled={disabled}
      onClick={handleImageClick}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        disabled={disabled}
        style={{ display: 'none' }}
      />
      <ImagePlaceholder>
        {imageUrl ? (
          <>
            <UploadedImage
              src={imageUrl}
              alt="Uploaded"
              key={imageUrl}
            />
            {showDeleteButton && !disabled && (
              <ImageDeleteButton
                className="imageDeleteButton"
                onClick={handleDeleteClick}
                type="button"
                title="이미지 삭제"
              >
                <DeleteIcon>×</DeleteIcon>
              </ImageDeleteButton>
            )}
          </>
        ) : (
          <UploadIcon>
            <PlusIcon />
          </UploadIcon>
        )}
      </ImagePlaceholder>
    </ImageUpload>
  );
};


// Vimeo 섹션 컴포넌트
const VimeoSectionComponent: React.FC<{
  vimeoId: string;
  onVimeoIdChange: (vimeoId: string) => void;
  onSync: () => void;
}> = ({ vimeoId, onVimeoIdChange, onSync }) => (
  <VimeoSection>
    <InputGroup>
      <Label>VIMEO ID</Label>
      <InputWrapper>
        <Input
          type="text"
          placeholder="Vimeo 비디오 ID를 입력해주세요."
          value={vimeoId}
          onChange={(e) => onVimeoIdChange(e.target.value)}
        />
      </InputWrapper>
    </InputGroup>
    <SyncButton
      onClick={onSync}
    >
      <SyncButtonText>동기화</SyncButtonText>
    </SyncButton>
  </VimeoSection>
);

// 기본 정보 섹션 컴포넌트
const BasicInfoSection: React.FC<{
  title: string;
  onTitleChange: (title: string) => void;
  categories: LectureCategory[];
  categoryId: number;
  onCategoryChange: (categoryId: number) => void;
  isTitleSynced: boolean;
}> = ({ title, onTitleChange, categories, categoryId, onCategoryChange, isTitleSynced }) => (
  <>
    <InputGroup>
      <Label>제목</Label>
      <InputWrapper>
        <Input
          type="text"
          placeholder="제목을 입력해주세요."
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          disabled={isTitleSynced}
        />
      </InputWrapper>
    </InputGroup>
    <InputGroup>
      <Label>카테고리</Label>
      <InputWrapper>
        <Select
          value={categoryId}
          onChange={(e) => onCategoryChange(Number(e.target.value))}
        >
          <option value={0}>카테고리를 선택해주세요.</option>
          {categories.map(category => (
            <option key={category.id} value={category.id}>{category.name}</option>
          ))}
        </Select>
      </InputWrapper>
    </InputGroup>
  </>
);

// 이미지 업로드 섹션 컴포넌트
const ImageUploadSectionComponent: React.FC<{
  onThumbnailChange: (file: File | null) => void;
  onBannerChange: (file: File | null) => void;
  lectureId?: number;
  isEditMode?: boolean;
  imageUrls?: {
    thumbnail?: string | null;
    banner?: string | null;
  };
  onImageUrlsUpdate?: (urls: { thumbnail?: string; banner?: string }) => void;
  imageKey?: number;
  onImageKeyUpdate?: () => void;
}> = ({ onThumbnailChange, onBannerChange, lectureId, isEditMode, imageUrls, onImageUrlsUpdate, imageKey = 0, onImageKeyUpdate }) => {
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [deletingThumbnail, setDeletingThumbnail] = useState(false);
  const [deletingBanner, setDeletingBanner] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState(imageUrls?.thumbnail);
  const [bannerUrl, setBannerUrl] = useState(imageUrls?.banner);

  React.useEffect(() => {
    setThumbnailUrl(imageUrls?.thumbnail || null);
    setBannerUrl(imageUrls?.banner || null);
  }, [imageUrls?.thumbnail, imageUrls?.banner]);

  const handleThumbnailChange = async (file: File | null) => {
    if (!file) {
      onThumbnailChange(file);
      return;
    }

    if (isEditMode && lectureId) {
      // 수정 모드에서는 바로 업로드
      setUploadingThumbnail(true);
      try {
        const result = await updateLectureThumbnail(lectureId, file);
        /// console.log('썸네일 업로드 성공, 새 URL:', result.thumbnail_url);
        alert('썸네일이 성공적으로 업로드되었습니다.');

        // 서버에서 반환한 URL로 업데이트
        setThumbnailUrl(result.thumbnail_url);
        onThumbnailChange(file); // 상태도 업데이트

        // 부모 컴포넌트에 URL 업데이트 알림
        if (onImageUrlsUpdate) {
          onImageUrlsUpdate({ thumbnail: result.thumbnail_url });
        }
      } catch (error: any) {
        /// console.error('썸네일 업로드 실패:', error);
        alert(`썸네일 업로드에 실패했습니다: ${error.message}`);
      } finally {
        setUploadingThumbnail(false);
      }
    } else {
      // 등록 모드에서는 기존 방식대로 처리
      onThumbnailChange(file);
    }
  };

  const handleBannerChange = async (file: File | null) => {
    if (!file) {
      onBannerChange(file);
      return;
    }

    if (isEditMode && lectureId) {
      // 수정 모드에서는 바로 업로드
      setUploadingBanner(true);
      try {
        const result = await updateLectureBanner(lectureId, file);
        /// console.log('배너 업로드 성공, 새 URL:', result.banner_url);
        alert('배너가 성공적으로 업로드되었습니다.');

        // 서버에서 반환한 URL로 업데이트
        setBannerUrl(result.banner_url);
        onBannerChange(file); // 상태도 업데이트

        // 부모 컴포넌트에 URL 업데이트 알림
        if (onImageUrlsUpdate) {
          onImageUrlsUpdate({ banner: result.banner_url });
        }
      } catch (error: any) {
        /// console.error('배너 업로드 실패:', error);
        alert(`${error.message}`);
      } finally {
        setUploadingBanner(false);
      }
    } else {
      // 등록 모드에서는 기존 방식대로 처리
      onBannerChange(file);
    }
  };

  const handleDeleteThumbnail = async () => {
    if (!isEditMode || !lectureId) return;

    if (!window.confirm('썸네일을 삭제하시겠습니까?\nVimeo의 썸네일이 적용됩니다.')) {
      return;
    }

    setDeletingThumbnail(true);
    try {
      await deleteLectureThumbnail(lectureId);
      alert('썸네일이 삭제되었습니다.');

      // 서버에서 최신 데이터 조회하여 URL 갱신
      const updatedDetail = await getAdminLectureDetail(lectureId);
      const newThumbnailUrl = updatedDetail.thumbnail_url;

      // 부모 컴포넌트에 URL 업데이트 알림 (useEffect에서 상태 업데이트됨)
      if (onImageUrlsUpdate) {
        onImageUrlsUpdate({ thumbnail: newThumbnailUrl || undefined });
      }
      onThumbnailChange(null);

      // 강제 리렌더링을 위한 imageKey 증가 요청
      if (onImageKeyUpdate) {
        onImageKeyUpdate();
      }

      // 즉시 로컬 상태도 업데이트
      setThumbnailUrl(newThumbnailUrl);
    } catch (error: any) {
      alert(`썸네일 삭제에 실패했습니다: ${error.message}`);
    } finally {
      setDeletingThumbnail(false);
    }
  };

  const handleDeleteBanner = async () => {
    if (!isEditMode || !lectureId) return;

    if (!window.confirm('배너를 삭제하시겠습니까?')) {
      return;
    }

    setDeletingBanner(true);
    try {
      await deleteLectureBanner(lectureId);
      alert('배너가 삭제되었습니다.');

      // 서버에서 최신 데이터 조회하여 URL 갱신
      const updatedDetail = await getAdminLectureDetail(lectureId);
      const newBannerUrl = updatedDetail.banner_url;

      // 부모 컴포넌트에 URL 업데이트 알림 (useEffect에서 상태 업데이트됨)
      if (onImageUrlsUpdate) {
        onImageUrlsUpdate({ banner: newBannerUrl || undefined });
      }
      onBannerChange(null);

      // 강제 리렌더링을 위한 imageKey 증가 요청
      if (onImageKeyUpdate) {
        onImageKeyUpdate();
      }

      // 즉시 로컬 상태도 업데이트
      setBannerUrl(newBannerUrl);
    } catch (error: any) {
      alert(`배너 삭제에 실패했습니다: ${error.message}`);
    } finally {
      setDeletingBanner(false);
    }
  };

  return (
    <ImageSection>
      <ImageGroup>
        <Label>썸네일</Label>
        <ThumbnailUpload
          as="div"
          key={`thumbnail-${imageKey}-${thumbnailUrl || 'null'}`}
          $disabled={uploadingThumbnail || deletingThumbnail}
          onClick={(e: React.MouseEvent) => {
            if ((e.target as HTMLElement).closest('.imageDeleteButton')) return;
            if (!(uploadingThumbnail || deletingThumbnail)) {
              (e.currentTarget.querySelector('input[type="file"]') as HTMLInputElement | null)?.click();
            }
          }}
        >
          <ImageUploadComponent
            onImageChange={handleThumbnailChange}
            disabled={uploadingThumbnail || deletingThumbnail}
            initialImageUrl={thumbnailUrl}
            onDelete={handleDeleteThumbnail}
            showDeleteButton={isEditMode && !!thumbnailUrl}
          />
        </ThumbnailUpload>
        {uploadingThumbnail && (
          <UploadingText>썸네일 업로드 중...</UploadingText>
        )}
        {deletingThumbnail && (
          <UploadingText>썸네일 삭제 중...</UploadingText>
        )}
      </ImageGroup>
      <ImageGroup>
        <Label>배너</Label>
        <BannerUpload
          as="div"
          key={`banner-${imageKey}-${bannerUrl || 'null'}`}
          $disabled={uploadingBanner || deletingBanner}
          onClick={(e: React.MouseEvent) => {
            if ((e.target as HTMLElement).closest('.imageDeleteButton')) return;
            if (!(uploadingBanner || deletingBanner)) {
              (e.currentTarget.querySelector('input[type="file"]') as HTMLInputElement | null)?.click();
            }
          }}
        >
          <ImageUploadComponent
            onImageChange={handleBannerChange}
            disabled={uploadingBanner || deletingBanner}
            initialImageUrl={bannerUrl}
            onDelete={handleDeleteBanner}
            showDeleteButton={isEditMode && !!bannerUrl}
          />
        </BannerUpload>
        {uploadingBanner && (
          <UploadingText>배너 업로드 중...</UploadingText>
        )}
        {deletingBanner && (
          <UploadingText>배너 삭제 중...</UploadingText>
        )}
      </ImageGroup>
    </ImageSection>
  );
};

// 강연자 정보 섹션 컴포넌트
const LecturerInfoSection: React.FC<{
  lecturerName: string;
  lecturerDepartment: string;
  lecturerEmail: string;
  onLecturerNameChange: (name: string) => void;
  onLecturerDepartmentChange: (department: string) => void;
  onLecturerEmailChange: (email: string) => void;
}> = ({ lecturerName, lecturerDepartment, lecturerEmail, onLecturerNameChange, onLecturerDepartmentChange, onLecturerEmailChange }) => (
  <>
    <LecturerSection>
      <InputGroup>
        <Label>강연자 이름</Label>
        <InputWrapper>
          <Input
            type="text"
            placeholder="강연자 이름을 입력해주세요."
            value={lecturerName}
            onChange={(e) => onLecturerNameChange(e.target.value)}
          />
        </InputWrapper>
      </InputGroup>
      <InputGroup>
        <Label>강연자 소속</Label>
        <InputWrapper>
          <Input
            type="text"
            placeholder="강연자 소속을 입력해주세요."
            value={lecturerDepartment}
            onChange={(e) => onLecturerDepartmentChange(e.target.value)}
          />
        </InputWrapper>
      </InputGroup>
    </LecturerSection>
    <InputGroup>
      <Label>강연자 이메일</Label>
      <InputWrapper>
        <Input
          type="email"
          placeholder="이메일을 입력해주세요."
          value={lecturerEmail}
          onChange={(e) => onLecturerEmailChange(e.target.value)}
        />
      </InputWrapper>
    </InputGroup>
  </>
);

// 강의 자료 섹션 컴포넌트
const MaterialsSectionComponent: React.FC<{
  onFilesChange: (files: File[]) => void;
  files: File[];
  lectureId?: number;
  isEditMode?: boolean;
}> = ({ onFilesChange, files = [], lectureId, isEditMode }) => {
  const [uploadedFiles, setUploadedFiles] = useState<{ id: string; url: string }[]>([]);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  // 강의자료 목록 조회
  const loadUploadedFiles = useCallback(async () => {
    if (!lectureId || !isEditMode) return;

    try {
      const response = await getLectureFiles(lectureId);

      // response는 { lecture_id: number, file_map: Record<string, string> } 형태
      // file_map을 배열로 변환
      const filesArray = Object.entries(response.file_map || {}).map(([id, url]) => ({
        id,
        url
      }));

      setUploadedFiles(filesArray);
      /// console.log('강의자료 목록 로드됨:', filesArray);
    } catch (error) {
      /// console.error('강의자료 목록 조회 실패:', error);
    }
  }, [lectureId, isEditMode]);

  // 컴포넌트 마운트 시 강의자료 목록 조회
  useEffect(() => {
    loadUploadedFiles();
  }, [lectureId, isEditMode, loadUploadedFiles]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    /// console.log('강의자료 파일 선택:', selectedFiles.map(f => `${f.name} (${f.size} bytes)`));

    if (!lectureId || !isEditMode) {
      // 등록 모드에서는 기존 방식대로 처리
      onFilesChange(selectedFiles);
      return;
    }

    // 수정 모드에서는 바로 업로드
    if (selectedFiles.length > 0) {
      await handleUploadFiles(selectedFiles);
    }
  };

  const handleUploadFiles = async (filesToUpload: File[]) => {
    if (!lectureId) return;

    setUploading(true);
    try {
      const response = await uploadLectureFiles(lectureId, filesToUpload);
      /// console.log('강의자료 업로드 성공:', response);

      // 업로드된 파일 목록 새로고침
      await loadUploadedFiles();

      // 파일 입력 초기화
      const fileInput = document.getElementById('lecture-files') as HTMLInputElement;
      if (fileInput) fileInput.value = '';

      alert('강의자료가 성공적으로 업로드되었습니다.');
    } catch (error: any) {
      /// console.error('강의자료 업로드 실패:', error);
      alert(`강의자료 업로드에 실패했습니다: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    if (!lectureId) return;

    if (!window.confirm('이 강의자료를 삭제하시겠습니까?')) return;

    setDeleting(fileId);
    try {
      await deleteLectureFile(lectureId, parseInt(fileId, 10));
      /// console.log('강의자료 삭제 성공:', fileId);

      // 삭제된 파일 목록 새로고침
      await loadUploadedFiles();

      alert('강의자료가 성공적으로 삭제되었습니다.');
    } catch (error: any) {
      /// console.error('강의자료 삭제 실패:', error);
      alert(`강의자료 삭제에 실패했습니다: ${error.message}`);
    } finally {
      setDeleting(null);
    }
  };

  const removeFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    /// console.log('강의자료 파일 제거:', files[index]?.name, '남은 파일:', newFiles.map(f => f.name));
    onFilesChange(newFiles);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileNameFromUrl = (url: string) => {
    const urlParts = url.split('/');
    const fileName = urlParts[urlParts.length - 1];
    // URL 디코딩
    return decodeURIComponent(fileName);
  };

  return (
    <MaterialsSection>
      <InputGroup>
        <Label>강의 자료</Label>
        <MaterialsWrapper>
          <MaterialsDescription>
            {isEditMode
              ? '강의 자료 파일 업로드 (복수의 자료 업로드 필요 시 동시에 업로드)'
              : '강의 자료 파일 업로드 (복수의 자료 업로드 필요 시 동시에 업로드)'
            }
          </MaterialsDescription>
          <input
            type="file"
            multiple
            onChange={handleFileChange}
            style={{ display: 'none' }}
            id="lecture-files"
            disabled={uploading}
          />
          <FileUploadButton
            htmlFor="lecture-files"
            $disabled={uploading}
          >
            <FileUploadButtonText>
              {uploading ? '업로드 중...' : '파일 업로드'}
            </FileUploadButtonText>
          </FileUploadButton>

          {/* 등록 모드: 선택된 파일 목록 */}
          {!isEditMode && files && files.length > 0 && (
            <FileList>
              <FileListTitle>선택된 파일 ({files.length}개)</FileListTitle>
              {files.map((file, index) => (
                <FileItem key={index}>
                  <FileInfo>
                    <FileName>{file.name}</FileName>
                    <div>{formatFileSize(file.size)}</div>
                  </FileInfo>
                  <RemoveFileButton
                    type="button"
                    onClick={() => removeFile(index)}
                    title="파일 제거"
                  >
                    ✕
                  </RemoveFileButton>
                </FileItem>
              ))}
            </FileList>
          )}


        </MaterialsWrapper>
            {/* 수정 모드: 업로드된 파일 목록 */}
            {isEditMode && uploadedFiles && uploadedFiles.length > 0 && (
            <FileList>
              <FileListTitle>
                업로드된 강의자료 ({uploadedFiles.length}개)
              </FileListTitle>
              {uploadedFiles.map((file) => (
                <FileItem key={file.id}>
                  <FileInfo>
                    <FileName>{getFileNameFromUrl(file.url)}</FileName>
                    <FileUrl>
                      <FileLink href={file.url} target="_blank" rel="noopener noreferrer">
                        파일 보기
                      </FileLink>
                    </FileUrl>
                  </FileInfo>
                  <RemoveFileButton
                    type="button"
                    onClick={() => handleDeleteFile(file.id)}
                    title="파일 삭제"
                    disabled={deleting === file.id}
                  >
                    {deleting === file.id ? '삭제 중...' : '✕'}
                  </RemoveFileButton>
                </FileItem>
              ))}
            </FileList>
          )}
      </InputGroup>
    </MaterialsSection>
  );
};

// 이미지 업로드 섹션들 컴포넌트
const ImageUploadSections: React.FC<{
  onLectureIntroChange: (file: File | null) => void;
  onLecturerIntroChange: (file: File | null) => void;
  onReviewChange: (file: File | null) => void;
  files?: {
    lectureIntro?: File | null;
    lecturerIntro?: File | null;
    review?: File | null;
  };
  imageUrls?: {
    lectureIntro?: string | null;
    lecturerIntro?: string | null;
    review?: string | null;
  };
  lectureId?: number;
  isEditMode?: boolean;
  onImageUrlsUpdate?: (urls: { lectureIntro?: string; lecturerIntro?: string; review?: string }) => void;
}> = ({ onLectureIntroChange, onLecturerIntroChange, onReviewChange, files = {}, imageUrls, lectureId, isEditMode, onImageUrlsUpdate }) => {
  const [previewUrls, setPreviewUrls] = useState<{
    lectureIntro?: string;
    lecturerIntro?: string;
    review?: string;
  }>({});

  const [uploading, setUploading] = useState<{
    lectureIntro: boolean;
    lecturerIntro: boolean;
    review: boolean;
  }>({
    lectureIntro: false,
    lecturerIntro: false,
    review: false,
  });

  // imageUrls가 변경되면 previewUrls 업데이트
  useEffect(() => {
    setPreviewUrls({
      lectureIntro: imageUrls?.lectureIntro || undefined,
      lecturerIntro: imageUrls?.lecturerIntro || undefined,
      review: imageUrls?.review || undefined,
    });
  }, [imageUrls]);

  const handleFileChange = async (type: 'lectureIntro' | 'lecturerIntro' | 'review', file: File | null) => {
    // /// console.log(`${type} 파일 선택:`, file);

    // 기존 URL 정리
    if (previewUrls[type] && !imageUrls?.[type]) {
      URL.revokeObjectURL(previewUrls[type]!);
    }

    if (file) {
      // 미리보기 URL 생성
      const url = URL.createObjectURL(file);
      setPreviewUrls(prev => ({ ...prev, [type]: url }));

      // 수정 모드이고 lectureId가 있으면 바로 업로드
      if (isEditMode && lectureId) {
        setUploading(prev => ({ ...prev, [type]: true }));

        try {
          if (type === 'lectureIntro') {
            await updateLectureIntroduction(lectureId, file);
            alert('강의 소개 이미지가 성공적으로 업로드되었습니다.');
          } else if (type === 'lecturerIntro') {
            await updateLecturerIntroduction(lectureId, file);
            alert('강사 소개 이미지가 성공적으로 업로드되었습니다.');
          } else if (type === 'review') {
            await updateLectureReview(lectureId, file);
            alert('수강 후기 이미지가 성공적으로 업로드되었습니다.');
          }

          // 업로드 후 최신 상세 정보로 실제 URL 반영
          const updatedDetail = await getAdminLectureDetail(lectureId);
          const updatedUrls = {
            lectureIntro: updatedDetail.introduction || undefined,
            lecturerIntro: updatedDetail.lecturer_introduction || undefined,
            review: updatedDetail.review || undefined,
          } as { lectureIntro?: string; lecturerIntro?: string; review?: string };

          // 해당 타입의 미리보기 이미지도 실제 URL로 교체
          if (type === 'lectureIntro' && updatedUrls.lectureIntro) {
            setPreviewUrls(prev => ({ ...prev, lectureIntro: updatedUrls.lectureIntro }));
          } else if (type === 'lecturerIntro' && updatedUrls.lecturerIntro) {
            setPreviewUrls(prev => ({ ...prev, lecturerIntro: updatedUrls.lecturerIntro }));
          } else if (type === 'review' && updatedUrls.review) {
            setPreviewUrls(prev => ({ ...prev, review: updatedUrls.review }));
          }

          // 부모에 URL 변경 전달 (즉시 재랜더링)
          if (onImageUrlsUpdate) {
            onImageUrlsUpdate(updatedUrls);
          }

          /// console.log(`${type} 이미지 업로드 성공, URL 반영 완료`);
        } catch (error: any) {
          /// console.error(`${type} 이미지 업로드 실패:`, error);
          alert(`${getTypeDisplayName(type)} 이미지 업로드에 실패했습니다: ${error.message}`);

          // 실패한 경우 미리보기도 원복
          setPreviewUrls(prev => ({ ...prev, [type]: imageUrls?.[type] || undefined }));
          file = null;
        } finally {
          setUploading(prev => ({ ...prev, [type]: false }));
        }
      }

      // 부모 컴포넌트에 파일 전달
      if (type === 'lectureIntro') onLectureIntroChange(file);
      else if (type === 'lecturerIntro') onLecturerIntroChange(file);
      else if (type === 'review') onReviewChange(file);
    } else {
      setPreviewUrls(prev => ({ ...prev, [type]: undefined }));

      // 부모 컴포넌트에 null 전달
      if (type === 'lectureIntro') onLectureIntroChange(null);
      else if (type === 'lecturerIntro') onLecturerIntroChange(null);
      else if (type === 'review') onReviewChange(null);
    }
  };

  const getTypeDisplayName = (type: 'lectureIntro' | 'lecturerIntro' | 'review') => {
    switch (type) {
      case 'lectureIntro': return '강의 소개';
      case 'lecturerIntro': return '강사 소개';
      case 'review': return '수강 후기';
    }
  };

  return (
    <ImageUploadSection>
      {[
        { title: '강의 소개', type: 'lectureIntro' as const, onChange: onLectureIntroChange },
        { title: '강연자 소개', type: 'lecturerIntro' as const, onChange: onLecturerIntroChange },
        { title: '수강 후기', type: 'review' as const, onChange: onReviewChange }
      ].map((item, index) => (
        <ImageUploadGroup key={item.title}>
          <Label>{item.title}</Label>
          <ImageUploadWrapper>
            {/* 미리보기가 없을 때만 안내문구 표시 */}
            {!previewUrls[item.type] && (
              <ImageUploadDescription>
                배경 유무에 따라 폭 3840px 혹은 1200px의 SVG 파일 업로드
              </ImageUploadDescription>
            )}

            {/* 미리보기 영역 */}
            {previewUrls[item.type] && (
              <ImagePreview>
                <PreviewImage
                  src={previewUrls[item.type]}
                  alt={`${item.title} 미리보기`}
                />
              </ImagePreview>
            )}

            {/* 업로드 상태 표시 */}
            {uploading[item.type] && (
              <UploadingText>
                {getTypeDisplayName(item.type)} 업로드 중...
              </UploadingText>
            )}

            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleFileChange(item.type, e.target.files?.[0] || null)}
              style={{ display: 'none' }}
              id={`image-upload-${index}`}
              disabled={uploading[item.type]}
            />
            <FileUploadButton
              htmlFor={`image-upload-${index}`}
              $disabled={uploading[item.type]}
            >
              <FileUploadButtonText>
                {uploading[item.type]
                  ? '업로드 중...'
                  : '이미지 업로드'
                }
              </FileUploadButtonText>
            </FileUploadButton>
          </ImageUploadWrapper>
        </ImageUploadGroup>
      ))}
    </ImageUploadSection>
  );
};

// 추천 강의 섹션 컴포넌트
const RecommendedSectionComponent: React.FC<{
  lectureList: LectureListItemType[];
  recommendedLectures: number[];
  onRecommendedChange: (lectureId: number, checked: boolean) => void;
  onShowLectureList: () => void;
}> = ({ lectureList, recommendedLectures, onRecommendedChange, onShowLectureList }) => {
  // 선택된 강의들만 필터링
  const selectedLectures = lectureList.filter(lecture =>
    recommendedLectures.includes(lecture.lecture_info.id)
  );

  return (
    <RecommendedSection>
      <Label>추천 강의</Label>
      <RecommendedImageSection>
        {selectedLectures.map((lecture) => (
          <RecommendedImageGroup key={lecture.lecture_info.id}>
            <RecommendedImageUpload>
              <RecommendedImage
                src={lecture.lecture_info.thumbnail_url}
                alt={lecture.lecture_info.title}
                onError={(e) => {
                  e.currentTarget.src = '/images/no-image-dummy.png';
                }}
              />
              <RecommendedRemoveButton
                onClick={() => onRecommendedChange(lecture.lecture_info.id, false)}
                title="제거"
              >
                ×
              </RecommendedRemoveButton>
            </RecommendedImageUpload>
            <RecommendedImageTitle>{lecture.lecture_info.title}</RecommendedImageTitle>
          </RecommendedImageGroup>
        ))}
        {selectedLectures && selectedLectures.length < 4 && (
          <RecommendedImageGroup>
            <RecommendedImageUpload onClick={onShowLectureList}>
              <RecommendedImagePlaceholder>
                <RecommendedPlusContainer>
                  <UploadIcon>
                    <PlusIcon />
                  </UploadIcon>
                </RecommendedPlusContainer>
              </RecommendedImagePlaceholder>
            </RecommendedImageUpload>
          </RecommendedImageGroup>
        )}
      </RecommendedImageSection>
    </RecommendedSection>
  );
};

// FAQ 섹션 컴포넌트
const FaqSectionComponent: React.FC<{
  faqQuestions: string[];
  faqAnswers: string[];
  onFaqChange: (index: number, type: 'question' | 'answer', value: string) => void;
  onAddFaq: (index: number) => void;
  onRemoveFaq: (index: number) => void;
  onEnsureMinimumFaq?: () => void;
}> = ({ faqQuestions, faqAnswers, onFaqChange, onAddFaq, onRemoveFaq, onEnsureMinimumFaq }) => {
  // FAQ가 완전히 비어있을 때 최소 하나의 항목 생성
  React.useEffect(() => {
    if (faqQuestions.length === 0 && onEnsureMinimumFaq) {
      onEnsureMinimumFaq();
    }
  }, [faqQuestions.length, onEnsureMinimumFaq]);

  return (
    <FaqSection>
      <Label>FAQ</Label>
      <FaqList>
        {faqQuestions.map((question, index) => (
          <FaqItem key={index}>
            <FaqInputGroup>
              <FaqQuestionWrapper>
                <FaqInput
                  type="text"
                  placeholder={`FAQ ${index + 1}의 질문`}
                  value={question}
                  onChange={(e) => onFaqChange(index, 'question', e.target.value)}
                />
              </FaqQuestionWrapper>
              <FaqAnswerWrapper>
                <FaqInput
                  type="text"
                  placeholder={`FAQ ${index + 1}의 답변`}
                  value={faqAnswers[index]}
                  onChange={(e) => onFaqChange(index, 'answer', e.target.value)}
                />
              </FaqAnswerWrapper>
            </FaqInputGroup>
            <FaqButtons>
              <FaqAddButton
                onClick={() => onAddFaq(index)}
                type="button"
                title="아래에 FAQ 추가"
              >
                +
              </FaqAddButton>
              <FaqRemoveButton
                onClick={() => onRemoveFaq(index)}
                disabled={faqQuestions && faqQuestions.length <= 1}
                type="button"
                title="FAQ 삭제"
              >
                -
              </FaqRemoveButton>
            </FaqButtons>
          </FaqItem>
        ))}
      </FaqList>
    </FaqSection>
  );
};

// 강의 목록 모달 컴포넌트
const LectureListModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  lectureList: LectureListItemType[];
  selectedLectures: number[];
  onLectureSelect: (lectureId: number, checked: boolean) => void;
}> = ({ isOpen, onClose, lectureList, selectedLectures, onLectureSelect }) => {
  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>강의 목록</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>
        <ModalBody>
          <LectureListGrid>
            {lectureList.map((lecture) => (
              <LectureListItem key={lecture.lecture_info.id}>
                <LectureCheckbox
                  type="checkbox"
                  checked={selectedLectures.includes(lecture.lecture_info.id)}
                  onChange={(e) => onLectureSelect(lecture.lecture_info.id, e.target.checked)}
                />
                <LectureItemImageContainer>
                  <LectureItemThumbnail
                    src={lecture.lecture_info.thumbnail_url}
                    alt={lecture.lecture_info.title}
                    onError={(e) => {
                      e.currentTarget.src = '/images/no-image-dummy.png';
                    }}
                  />
                </LectureItemImageContainer>
                <LectureItemInfo>
                  <LectureItemTitle>{lecture.lecture_info.title}</LectureItemTitle>
                  <LectureItemTags>
                    {lecture.lecture_info.tags.map((tag: string, index: number) => (
                      <Tag key={index}>{tag}</Tag>
                    ))}
                  </LectureItemTags>
                </LectureItemInfo>
              </LectureListItem>
            ))}
          </LectureListGrid>
        </ModalBody>
        <ModalFooter>
          <ConfirmButton onClick={onClose}>
            확인
          </ConfirmButton>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

// 저장 액션 섹션 컴포넌트
const SaveActionsSection: React.FC<{
  onCreateLecture?: () => void;
  onTempSave: () => void;
  onSave: () => void;
  onDelete?: () => void;
  saving: boolean;
  isEditMode?: boolean;
  hasLectureId?: boolean;
}> = ({ onCreateLecture, onTempSave, onSave, onDelete, saving, isEditMode = false, hasLectureId = false }) => {
  // 강의 ID가 없으면 강의 등록하기 버튼만 보임
  if (!hasLectureId && !isEditMode) {
    return (
      <ButtonSection>
        <ButtonGroup>
          <SaveWrapper>
            <SaveButton
              onClick={onCreateLecture}
              disabled={saving}
            >
              <SaveButtonText>
                {saving ? '생성 중...' : '강의 등록하기'}
              </SaveButtonText>
            </SaveButton>
          </SaveWrapper>
        </ButtonGroup>
      </ButtonSection>
    );
  }

  // 강의 ID가 있으면 임시저장/등록하기 버튼 표시
  return (
    <ButtonSection>
      <ButtonGroup>
        <TempSaveWrapper>
          {isEditMode && onDelete && (
              <TempSaveButton
                onClick={onDelete}
                disabled={saving}
              >
                <TempSaveButtonText>
                  {saving ? '삭제 중...' : '강의 삭제'}
                </TempSaveButtonText>
              </TempSaveButton>
        )}
          <TempSaveButton
            onClick={onTempSave}
            disabled={saving}
          >
            <TempSaveButtonText>
              {saving ? '저장 중...' : '임시 저장'}
            </TempSaveButtonText>
          </TempSaveButton>
        </TempSaveWrapper>
        <SaveWrapper>
          <SaveButton
            onClick={onSave}
            disabled={saving}
          >
            <SaveButtonText>
              {saving ? (isEditMode ? '수정 중...' : '등록 중...') : (isEditMode ? '수정 완료' : '저장하기')}
            </SaveButtonText>
          </SaveButton>
        </SaveWrapper>

      </ButtonGroup>
    </ButtonSection>
  );
};

const LectureRegisterComponent: React.FC = () => {
  // 상태 관리
  const [categories, setCategories] = useState<LectureCategory[]>([]);
  const [lectureList, setLectureList] = useState<LectureListItemType[]>([]);
  const [lecturers, setLecturers] = useState<Lecturer[]>([]);
  const [lecturerMode, setLecturerMode] = useState<'select' | 'create'>('select');
  const [selectedLecturerId, setSelectedLecturerId] = useState<number | null>(null);

  // 강연자 선택 시 폼 데이터 자동 채우기
  const handleLecturerSelect = (lecturerId: number | null) => {
    setSelectedLecturerId(lecturerId);
    if (lecturerId) {
      const selectedLecturer = lecturers.find(l => l.id === lecturerId);
      if (selectedLecturer) {
        handleInputChange('lecturerName', selectedLecturer.name);
        handleInputChange('lecturerDepartment', selectedLecturer.introduction || '');
        // 이메일이 있으면 자동으로 채우고, 없으면 빈 값으로 유지
        handleInputChange('lecturerEmail', selectedLecturer.email || '');
      }
    }
  };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentLectureId, setCurrentLectureId] = useState<number | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [lectureDetail, setLectureDetail] = useState<AdminLectureDetail | null>(null); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [isTitleSynced, setIsTitleSynced] = useState(false); // 동기화 상태 추가
  const [imageKey, setImageKey] = useState(0); // 이미지 컴포넌트 강제 리렌더링용

  // 이미지 URL 상태
  const [imageUrls, setImageUrls] = useState<{
    thumbnail?: string | null;
    banner?: string | null;
    lectureIntro?: string | null;
    lecturerIntro?: string | null;
    review?: string | null;
  }>({});

  // 폼 데이터 상태
  const [formData, setFormData] = useState<{
    vimeoId: string;
    title: string;
    categoryId: number;
    lecturerName: string;
    lecturerDepartment: string;
    lecturerEmail: string;
    faqQuestions: string[];
    faqAnswers: string[];
  }>({
    vimeoId: '',
    title: '',
    categoryId: 0,
    lecturerName: '',
    lecturerDepartment: '',
    lecturerEmail: '',
    faqQuestions: [''],
    faqAnswers: [''],
  });

  // 파일 상태
  const [files, setFiles] = useState<{
    thumbnail?: File | null;
    banner?: File | null;
    lectureFiles?: File[] | null;
    lectureIntro?: File | null;
    lecturerIntro?: File | null;
    review?: File | null;
  }>({});

  // 추천 강의 상태
  const [recommendedLectures, setRecommendedLectures] = useState<number[]>([]);

  // 모달 상태
  const [isLectureListModalOpen, setIsLectureListModalOpen] = useState(false);

  // URL 쿼리 파라미터 확인 및 초기 데이터 로드
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const lectureId = urlParams.get('lectureId');

    if (lectureId) {
      const id = parseInt(lectureId, 10);
      if (!isNaN(id)) {
        setCurrentLectureId(id);
        setIsEditMode(true);
        loadLectureDetail(id);
      }
    }

    loadInitialData();
  }, []);

  // URL 변경 감지 (popstate 이벤트)
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const lectureId = urlParams.get('lectureId');

      if (lectureId) {
        const id = parseInt(lectureId, 10);
        if (!isNaN(id)) {
          setCurrentLectureId(id);
          setIsEditMode(true);
          loadLectureDetail(id);
        }
      } else {
        setCurrentLectureId(null);
        setIsEditMode(false);
        // 폼 초기화
        setFormData({
          vimeoId: '',
          title: '',
          categoryId: 0,
          lecturerName: '',
          lecturerDepartment: '',
          lecturerEmail: '',
          faqQuestions: [''],
          faqAnswers: [''],
        });
        setImageUrls({});
        setImageKey(prev => prev + 1); // 이미지 컴포넌트 강제 리렌더링
        setFiles({
          thumbnail: null,
          banner: null,
          lectureFiles: null,
          lectureIntro: null,
          lecturerIntro: null,
          review: null
        });
        setRecommendedLectures([]);
        setLecturerMode('select');
        setSelectedLecturerId(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      // /// console.log('초기 데이터 로드 시작');

      const [categoriesData, lectureListData, lecturersData] = await Promise.all([
        getLectureCategories(),
        getLectureList(0, 1000000),
        getLecturers().catch(() => [])
      ]);

      setCategories(categoriesData);
      setLectureList(lectureListData.content);
      setLecturers(lecturersData);

      // console.log('초기 데이터 로드 성공:', {
      //   categoriesCount: categoriesData?.length || 0,
      //   lecturesCount: lectureListData?.content?.length || 0,
      //   lecturersCount: lecturersData?.length || 0,
      // });
    } catch (err: any) {
      /// console.error('초기 데이터 로드 실패:', err);
      const errorMessage = err.message || '데이터를 불러오는데 실패했습니다.';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadLectureDetail = async (lectureId: number) => {
    try {
      // /// console.log('관리자 강의 상세 정보 로드 시작:', lectureId);
      const detail = await getAdminLectureDetail(lectureId);
      setLectureDetail(detail);

      // 폼 데이터에 강의 상세 정보 설정
      setFormData(prev => ({
        ...prev,
        vimeoId: detail.vimeo_id,
        title: detail.title,
        categoryId: detail.category_id,
        lecturerName: detail.lecturer_name,
        lecturerDepartment: detail.lecturer_department,
        lecturerEmail: detail.lecturer_email,
        faqQuestions: detail.faqs && detail.faqs.length > 0 ? detail.faqs.map(faq => faq.question) : [''],
        faqAnswers: detail.faqs && detail.faqs.length > 0 ? detail.faqs.map(faq => faq.answer) : ['']
      }));

      // 이미지 URL들 설정
      setImageUrls({
        thumbnail: detail.thumbnail_url,
        banner: detail.banner_url,
        lectureIntro: detail.introduction,
        lecturerIntro: detail.lecturer_introduction,
        review: detail.review,
      });

      // 추천 강의 설정
      if (detail.recommended_lecture_ids && detail.recommended_lecture_ids.length > 0) {
        setRecommendedLectures(detail.recommended_lecture_ids);
      }

      /// console.log('관리자 강의 상세 정보 로드 완료:', detail);
    } catch (error) {
      /// console.error('관리자 강의 상세 정보 로드 실패:', error);
      alert('강의 상세 정보를 불러오는데 실패했습니다.');
    }
  };

  // 강연자 목록과 폼 데이터가 모두 로드된 후 매칭되는 강연자 자동 선택
  useEffect(() => {
    if (lecturers.length > 0 && formData.lecturerName && formData.lecturerDepartment) {
      const matchingLecturer = lecturers.find(lecturer =>
        lecturer.name === formData.lecturerName &&
        lecturer.introduction === formData.lecturerDepartment
      );

      if (matchingLecturer) {
        /// console.log('매칭되는 강연자 발견:', matchingLecturer);
        setSelectedLecturerId(matchingLecturer.id);
        setLecturerMode('select');
      } else {
        // console.log('매칭되는 강연자 없음:', {
        //   formName: formData.lecturerName,
        //   formDepartment: formData.lecturerDepartment,
        //   lecturers: lecturers.map(l => ({ name: l.name, introduction: l.introduction }))
        // });
      }
    }
  }, [lecturers, formData.lecturerName, formData.lecturerDepartment]);

  const handleVimeoIdChange = (vimeoId: string) => {
    setFormData(prev => ({ ...prev, vimeoId }));
    // Vimeo ID가 변경되면 동기화 상태 리셋
    setIsTitleSynced(false);
  };

  const handleVimeoSync = async () => {
    if (!formData.vimeoId.trim()) {
      alert('Vimeo ID를 입력해주세요.');
      return;
    }

    try {
      // /// console.log('Vimeo 정보 조회 요청:', formData.vimeoId);
      const info = await getVimeoInfo(formData.vimeoId.trim());
      setFormData(prev => ({
        ...prev,
        title: info.name
      }));
      setIsTitleSynced(true); // 동기화 상태 설정
      /// console.log('Vimeo 정보 조회 성공:', info);
    } catch (err: any) {
      /// console.error('Vimeo 정보 조회 실패:', err);
      const errorMessage = err.message || 'Vimeo 정보를 가져오는데 실패했습니다.';
      alert(errorMessage);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (type: keyof typeof files) => (file: File | File[] | null) => {
    // /// console.log(`파일 변경 감지 - 타입: ${type}`, file);
    setFiles(prev => {
      const newFiles = {
        ...prev,
        [type]: file
      };
      // /// console.log('새로운 파일 상태:', newFiles);
      return newFiles;
    });
  };

  const handleFaqChange = (index: number, type: 'question' | 'answer', value: string) => {
    setFormData(prev => ({
      ...prev,
      [`faq${type === 'question' ? 'Questions' : 'Answers'}`]: prev[`faq${type === 'question' ? 'Questions' : 'Answers'}`].map((item, i) =>
        i === index ? value : item
      )
    }));
  };

  // FAQ가 완전히 비어있을 때 최소 하나의 항목 생성
  const ensureMinimumFaq = () => {
    const hasValidFaq = formData.faqQuestions.some((question, index) =>
      question.trim() !== '' && formData.faqAnswers[index].trim() !== ''
    );

    if (!hasValidFaq) {
      setFormData(prev => ({
        ...prev,
        faqQuestions: [''],
        faqAnswers: ['']
      }));
    }
  };

  const addFaq = (index: number) => {
    setFormData(prev => ({
      ...prev,
      faqQuestions: [
        ...prev.faqQuestions.slice(0, index + 1),
        '',
        ...prev.faqQuestions.slice(index + 1)
      ],
      faqAnswers: [
        ...prev.faqAnswers.slice(0, index + 1),
        '',
        ...prev.faqAnswers.slice(index + 1)
      ]
    }));
  };

  const removeFaq = (index: number) => {
    if (formData.faqQuestions && formData.faqQuestions.length > 1) {
      setFormData(prev => ({
        ...prev,
        faqQuestions: prev.faqQuestions.filter((_, i) => i !== index),
        faqAnswers: prev.faqAnswers.filter((_, i) => i !== index)
      }));
    }
  };

  const handleRecommendedLectureChange = (lectureId: number, checked: boolean) => {
    if (checked) {
      setRecommendedLectures(prev => [...prev, lectureId]);
    } else {
      setRecommendedLectures(prev => prev.filter(id => id !== lectureId));
    }
  };

  // 파일 업로드 처리
  const uploadFiles = async (lectureId: number) => {
    try {
      // console.log('파일 업로드 시작:', {
      //   lectureId,
      //   files: {
      //     thumbnail: files.thumbnail?.name || '없음',
      //     banner: files.banner?.name || '없음',
      //     lectureFiles: files.lectureFiles?.map(f => f.name) || '없음',
      //     lectureIntro: files.lectureIntro?.name || '없음',
      //     lecturerIntro: files.lecturerIntro?.name || '없음',
      //     review: files.review?.name || '없음'
      //   }
      // });

      let uploadedCount = 0;
      const uploadedUrls: any = {};

      // 썸네일 업로드
      if (files.thumbnail) {
        /// console.log('썸네일 업로드 시작:', files.thumbnail.name, files.thumbnail.size, 'bytes');
        const result = await updateLectureThumbnail(lectureId, files.thumbnail);
        uploadedUrls.thumbnail = result.thumbnail_url;
        /// console.log('썸네일 업로드 완료, URL:', result.thumbnail_url);
        uploadedCount++;
      } else {
        /// console.log('썸네일 파일 없음');
      }

      // 배너 업로드
      if (files.banner) {
        /// console.log('배너 업로드 시작:', files.banner.name, files.banner.size, 'bytes');
        const result = await updateLectureBanner(lectureId, files.banner);
        uploadedUrls.banner = result.banner_url;
        /// console.log('배너 업로드 완료, URL:', result.banner_url);
        uploadedCount++;
      } else {
        /// console.log('배너 파일 없음');
      }

      // 강의 파일 업로드
      if (files.lectureFiles && files.lectureFiles.length > 0) {
        /// console.log('강의 파일 업로드 시작:', files.lectureFiles.map(f => `${f.name} (${f.size} bytes)`));
        await uploadLectureFiles(lectureId, files.lectureFiles);
        /// console.log('강의 파일 업로드 완료');
        uploadedCount += files.lectureFiles.length;
      } else {
        /// console.log('강의 파일 없음');
      }

      // 강의 소개 이미지 업로드
      if (files.lectureIntro) {
        /// console.log('강의 소개 이미지 업로드 시작:', files.lectureIntro.name, files.lectureIntro.size, 'bytes');
        await updateLectureIntroduction(lectureId, files.lectureIntro);
        /// console.log('강의 소개 이미지 업로드 완료');
        uploadedCount++;
      } else {
        /// console.log('강의 소개 이미지 없음');
      }

      // 강사 소개 이미지 업로드
      if (files.lecturerIntro) {
        /// console.log('강사 소개 이미지 업로드 시작:', files.lecturerIntro.name, files.lecturerIntro.size, 'bytes');
        await updateLecturerIntroduction(lectureId, files.lecturerIntro);
        /// console.log('강사 소개 이미지 업로드 완료');
        uploadedCount++;
      } else {
        /// console.log('강사 소개 이미지 없음');
      }

      // 수강 후기 이미지 업로드
      if (files.review) {
        /// console.log('수강 후기 이미지 업로드 시작:', files.review.name, files.review.size, 'bytes');
        await updateLectureReview(lectureId, files.review);
        /// console.log('수강 후기 이미지 업로드 완료');
        uploadedCount++;
      } else {
        /// console.log('수강 후기 이미지 없음');
      }

      /// console.log(`파일 업로드 완료: 총 ${uploadedCount}개 파일 업로드됨`);

      // 업로드된 URL들 반환
      return uploadedUrls;
    } catch (err: any) {
      /// console.error('파일 업로드 실패:', err);
      const errorMessage = err.message || '파일 업로드에 실패했습니다.';
      throw new Error(errorMessage);
    }
  };

  const handleShowLectureList = () => {
    setIsLectureListModalOpen(true);
  };

  const handleCloseLectureList = () => {
    setIsLectureListModalOpen(false);
  };

  const handleLectureSelect = (lectureId: number, checked: boolean) => {
    handleRecommendedLectureChange(lectureId, checked);
  };

  // 처음 강의 생성 (vimeoId, title, categoryId만 있을 때)
  const handleCreateLecture = async () => {
    try {
      setSaving(true);

      // 필수 필드 검증
      if (!formData.vimeoId.trim()) {
        alert('Vimeo ID를 입력해주세요.');
        return;
      }

      if (!formData.title.trim()) {
        alert('제목을 입력해주세요.');
        return;
      }

      if (!formData.categoryId || formData.categoryId === 0) {
        alert('카테고리를 선택해주세요.');
        return;
      }

      const lectureData = {
        vimeoId: formData.vimeoId.trim(),
        title: formData.title.trim(),
        lectureCategoryId: formData.categoryId,
        exposed: false,
      };

      /// console.log('강의 생성 요청:', lectureData);
      const result = await createLecture(lectureData as any);
      setCurrentLectureId(result.id);

      // URL에 lectureId 추가
      const url = new URL(window.location.href);
      url.searchParams.set('lectureId', result.id.toString());
      window.history.replaceState({}, '', url.toString());

      // 수정 모드로 전환
      setIsEditMode(true);

      alert('강의가 생성되었습니다. 추가 정보를 입력해주세요.');
    } catch (err: any) {
      /// console.error('강의 생성 실패:', err);
      const errorMessage = err.message || '강의 생성에 실패했습니다.';
      alert(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleTempSave = async () => {
    try {
      setSaving(true);

      if (!currentLectureId) {
        alert('먼저 강의를 생성해주세요.');
        return;
      }

      // 파일 업로드가 있으면 먼저 처리하고 URL 받기
      let newImageUrls: { thumbnail?: string; banner?: string } = {};
      if (files.thumbnail || files.banner || (files.lectureFiles && files.lectureFiles.length > 0) ||
          files.lectureIntro || files.lecturerIntro || files.review) {
        const uploadedUrls = await uploadFiles(currentLectureId);

        // 업로드된 URL들로 imageUrls 업데이트
        newImageUrls = {
          thumbnail: uploadedUrls.thumbnail || imageUrls.thumbnail,
          banner: uploadedUrls.banner || imageUrls.banner,
        };

        // 파일 업로드 후 강의 상세 정보를 다시 불러와서 전체 이미지 URL 업데이트
        const updatedDetail = await getAdminLectureDetail(currentLectureId);
        setImageUrls({
          thumbnail: updatedDetail.thumbnail_url,
          banner: updatedDetail.banner_url,
          lectureIntro: updatedDetail.introduction,
          lecturerIntro: updatedDetail.lecturer_introduction,
          review: updatedDetail.review,
        });
      }

      // 임시저장은 단순히 exposed를 false로 변경
      const updateData: LectureUpdateRequest = {
        vimeoId: formData.vimeoId.trim(),
        title: formData.title.trim(),
        categoryId: formData.categoryId,
        thumbnailUrl: newImageUrls.thumbnail || imageUrls.thumbnail || '',
        bannerUrl: newImageUrls.banner || imageUrls.banner || '',
        lecturerName: formData.lecturerName.trim(),
        lecturerDepartment: formData.lecturerDepartment.trim(),
        lecturerEmail: formData.lecturerEmail.trim(),
        introduction: imageUrls.lectureIntro || '',
        lecturerIntroduction: imageUrls.lecturerIntro || '',
        review: imageUrls.review || '',
        faqs: formData.faqQuestions.map((question, index) => ({
          question: question.trim(),
          answer: formData.faqAnswers[index].trim()
        })),
        recommendedLectureIds: recommendedLectures,
        exposed: false, // 임시저장은 항상 false
        ...(lecturerMode === 'select' && selectedLecturerId ? { lecturerId: selectedLecturerId } : { createLecturer: true }),
      };

      // console.log('임시 저장 요청 - 강연자 정보 확인:', {
      //   lecturerName: updateData.lecturerName,
      //   lecturerDepartment: updateData.lecturerDepartment,
      //   lecturerEmail: updateData.lecturerEmail,
      // });
      /// console.log('임시 저장 요청 전체:', updateData);
      await updateLecture(currentLectureId, updateData);

      alert('임시 저장되었습니다.');
    } catch (err: any) {
      /// console.error('임시 저장 실패:', err);
      const errorMessage = err.message || '임시 저장에 실패했습니다.';
      alert(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      if (!currentLectureId) {
        alert('먼저 강의를 생성해주세요.');
        return;
      }

      // 필수 필드 검증
      if (!formData.lecturerName.trim()) {
        alert('강연자 이름을 입력해주세요.');
        return;
      }

      if (!formData.lecturerDepartment.trim()) {
        alert('강연자 소속을 입력해주세요.');
        return;
      }

      if (!formData.lecturerEmail.trim()) {
        alert('강연자 이메일을 입력해주세요.');
        return;
      }

      // FAQ 검증 및 최소 항목 보장
      const hasValidFaq = formData.faqQuestions.some((question, index) =>
        question.trim() !== '' && formData.faqAnswers[index].trim() !== ''
      );

      if (!hasValidFaq) {
        // 유효한 FAQ가 없으면 최소 하나의 빈 항목 생성
        setFormData(prev => ({
          ...prev,
          faqQuestions: [''],
          faqAnswers: ['']
        }));
        alert('FAQ를 최소 하나 이상 입력해주세요.');
        return;
      }

      // 빈 질문이나 답변이 있는지 확인
      const hasEmptyFaq = formData.faqQuestions.some((q, index) =>
        q.trim() === '' || formData.faqAnswers[index].trim() === ''
      );

      if (hasEmptyFaq) {
        alert('FAQ의 질문과 답변을 모두 입력해주세요.');
        return;
      }

      // 파일 업로드를 먼저 수행하고 URL 받기
      let finalImageUrls = { ...imageUrls };

      if (files.thumbnail || files.banner || (files.lectureFiles && files.lectureFiles.length > 0) ||
          files.lectureIntro || files.lecturerIntro || files.review) {
        const uploadedUrls = await uploadFiles(currentLectureId);

        // 업로드된 URL들로 finalImageUrls 업데이트
        if (uploadedUrls.thumbnail) {
          finalImageUrls.thumbnail = uploadedUrls.thumbnail;
        }
        if (uploadedUrls.banner) {
          finalImageUrls.banner = uploadedUrls.banner;
        }

        // 파일 업로드 후 강의 상세 정보를 다시 불러와서 최신 URL 가져오기
        /// console.log('파일 업로드 완료, 최신 정보 조회 중...');
        const updatedDetail = await getAdminLectureDetail(currentLectureId);
        /// console.log('업데이트된 강의 상세 정보:', updatedDetail);

        // 최신 URL들로 최종 업데이트
        finalImageUrls = {
          thumbnail: updatedDetail.thumbnail_url,
          banner: updatedDetail.banner_url,
          lectureIntro: updatedDetail.introduction,
          lecturerIntro: updatedDetail.lecturer_introduction,
          review: updatedDetail.review,
        };

        // 상태도 업데이트
        setImageUrls(finalImageUrls);
      }

      // 최종 업데이트 데이터
      const updateData: LectureUpdateRequest = {
        vimeoId: formData.vimeoId.trim(),
        title: formData.title.trim(),
        categoryId: formData.categoryId,
        thumbnailUrl: finalImageUrls.thumbnail || '',
        bannerUrl: finalImageUrls.banner || '',
        lecturerName: formData.lecturerName.trim(),
        lecturerDepartment: formData.lecturerDepartment.trim(),
        lecturerEmail: formData.lecturerEmail.trim(),
        introduction: finalImageUrls.lectureIntro || '',
        lecturerIntroduction: finalImageUrls.lecturerIntro || '',
        review: finalImageUrls.review || '',
        faqs: formData.faqQuestions.map((question, index) => ({
          question: question.trim(),
          answer: formData.faqAnswers[index].trim()
        })),
        recommendedLectureIds: recommendedLectures,
        exposed: true, // 저장하기는 항상 true
        ...(lecturerMode === 'select' && selectedLecturerId ? { lecturerId: selectedLecturerId } : { createLecturer: true }),
      };

      // console.log('강의 저장 요청 - 강연자 정보 확인:', {
      //   lecturerName: updateData.lecturerName,
      //   lecturerDepartment: updateData.lecturerDepartment,
      //   lecturerEmail: updateData.lecturerEmail,
      // });
      /// console.log('강의 저장 요청 전체:', updateData);
      await updateLecture(currentLectureId, updateData);

      alert(isEditMode ? '강의가 성공적으로 수정되었습니다.' : '강의가 성공적으로 저장되었습니다.');
    } catch (err: any) {
      /// console.error('저장 실패:', err);
      const errorMessage = err.message || '저장에 실패했습니다.';
      alert(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!currentLectureId) {
      alert('삭제할 강의가 없습니다.');
      return;
    }

    if (!window.confirm('정말로 이 강의를 삭제하시겠습니까?\n\n삭제하면 다음 항목들이 함께 삭제됩니다:\n- 썸네일, 배너, 강의소개, 강사소개, 수강후기 이미지\n- 추천 강의 관계\n- FAQ 항목\n\n이 작업은 되돌릴 수 없습니다.')) {
      return;
    }

    try {
      setSaving(true);
      /// console.log('강의 삭제 요청:', currentLectureId);

      await deleteLecture(currentLectureId);

      alert('강의가 성공적으로 삭제되었습니다.');

      // 폼 초기화
      setFormData({
        vimeoId: '',
        title: '',
        categoryId: 0,
        lecturerName: '',
        lecturerDepartment: '',
        lecturerEmail: '',
        faqQuestions: [''],
        faqAnswers: [''],
      });
      setFiles({
        thumbnail: null,
        banner: null,
        lectureFiles: null,
        lectureIntro: null,
        lecturerIntro: null,
        review: null
      });
      setImageUrls({}); // 이미지 URL 상태도 초기화
      setImageKey(prev => prev + 1); // 이미지 컴포넌트 강제 리렌더링
      setRecommendedLectures([]);
      setCurrentLectureId(null);
      setIsEditMode(false);

      // URL에서 lectureId 제거
      const url = new URL(window.location.href);
      url.searchParams.delete('lectureId');
      window.history.replaceState({}, '', url.toString());

    } catch (err: any) {
      /// console.error('강의 삭제 실패:', err);
      const errorMessage = err.message || '강의 삭제에 실패했습니다.';
      alert(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle>
            {isEditMode ? '강의 수정' : '강의 등록'}
          </PageTitle>
        </PageHeader>
        <PageContent>
          <LoadingState>
            로딩 중...
          </LoadingState>
        </PageContent>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>
          {isEditMode ? '강의 수정' : '강의 등록'}
        </PageTitle>
      </PageHeader>

      <PageContent>
        <VimeoSectionComponent
          vimeoId={formData.vimeoId}
          onVimeoIdChange={(vimeoId) => handleVimeoIdChange(vimeoId)}
          onSync={handleVimeoSync}
        />

        <BasicInfoSection
          title={formData.title}
          onTitleChange={(title) => handleInputChange('title', title)}
          categories={categories}
          categoryId={formData.categoryId}
          onCategoryChange={(categoryId) => handleInputChange('categoryId', categoryId.toString())}
          isTitleSynced={isTitleSynced}
        />


        {/* 수정 모드일 때만 추가 정보 섹션들 표시 */}
        {isEditMode && (
          <>
            <ImageUploadSectionComponent
              onThumbnailChange={handleFileChange('thumbnail')}
              onBannerChange={handleFileChange('banner')}
              lectureId={currentLectureId || undefined}
              isEditMode={isEditMode}
              imageUrls={{
                thumbnail: imageUrls.thumbnail,
                banner: imageUrls.banner
              }}
              onImageUrlsUpdate={(urls) => {
                // 이미지 URL 즉시 업데이트
                setImageUrls(prev => ({
                  ...prev,
                  ...(urls.thumbnail !== undefined && { thumbnail: urls.thumbnail }),
                  ...(urls.banner !== undefined && { banner: urls.banner })
                }));
              }}
              imageKey={imageKey}
              onImageKeyUpdate={() => setImageKey(prev => prev + 1)}
            />


        {/* 강연자 선택/생성 컨트롤 */}
        <LecturerSelectionSection>
          <Label>강연자 선택</Label>
          <InputGroup>
            <InputWrapper>
              <label style={{ marginRight: '12px' }}>
                <input
                  type="radio"
                  name="lecturerMode"
                  checked={lecturerMode === 'select'}
                  onChange={() => setLecturerMode('select')}
                />
                <span style={{ marginLeft: 6 }}>기존 강연자 선택</span>
              </label>
              <label>
                <input
                  type="radio"
                  name="lecturerMode"
                  checked={lecturerMode === 'create'}
                  onChange={() => setLecturerMode('create')}
                />
                <span style={{ marginLeft: 6 }}>강연자 생성</span>
              </label>
            </InputWrapper>
          </InputGroup>
          {lecturerMode === 'select' && (
            <InputGroup>
              <InputWrapper>
                <Select
                  value={selectedLecturerId ?? ''}
                  onChange={(e) => handleLecturerSelect(e.target.value ? Number(e.target.value) : null)}
                >
                  {lecturers.map(l => (
                    <option key={l.id} value={l.id}>
                      {l.name}{l.introduction ? ` - ${l.introduction}` : ''}
                    </option>
                  ))}
                </Select>
              </InputWrapper>
            </InputGroup>
          )}
        </LecturerSelectionSection>

            <LecturerInfoSection
              lecturerName={formData.lecturerName}
              lecturerDepartment={formData.lecturerDepartment}
              lecturerEmail={formData.lecturerEmail}
              onLecturerNameChange={(name) => handleInputChange('lecturerName', name)}
              onLecturerDepartmentChange={(department) => handleInputChange('lecturerDepartment', department)}
              onLecturerEmailChange={(email) => handleInputChange('lecturerEmail', email)}
            />

            <MaterialsSectionComponent
              onFilesChange={(files) => handleFileChange('lectureFiles')(files)}
              files={files.lectureFiles || []}
              lectureId={currentLectureId || undefined}
              isEditMode={isEditMode}
            />

            <ImageUploadSections
              onLectureIntroChange={handleFileChange('lectureIntro')}
              onLecturerIntroChange={handleFileChange('lecturerIntro')}
              onReviewChange={handleFileChange('review')}
              files={{
                lectureIntro: files.lectureIntro,
                lecturerIntro: files.lecturerIntro,
                review: files.review
              }}
              imageUrls={{
                lectureIntro: imageUrls.lectureIntro,
                lecturerIntro: imageUrls.lecturerIntro,
                review: imageUrls.review
              }}
              lectureId={currentLectureId || undefined}
              isEditMode={isEditMode}
              onImageUrlsUpdate={(urls) => {
                setImageUrls(prev => ({
                  ...prev,
                  ...(urls.lectureIntro !== undefined && { lectureIntro: urls.lectureIntro }),
                  ...(urls.lecturerIntro !== undefined && { lecturerIntro: urls.lecturerIntro }),
                  ...(urls.review !== undefined && { review: urls.review }),
                }));
              }}
            />

            <RecommendedSectionComponent
              lectureList={lectureList}
              recommendedLectures={recommendedLectures}
              onRecommendedChange={handleRecommendedLectureChange}
              onShowLectureList={handleShowLectureList}
            />

            <FaqSectionComponent
              faqQuestions={formData.faqQuestions}
              faqAnswers={formData.faqAnswers}
              onFaqChange={handleFaqChange}
              onAddFaq={addFaq}
              onRemoveFaq={removeFaq}
              onEnsureMinimumFaq={ensureMinimumFaq}
            />
          </>
        )}

        <SaveActionsSection
          onCreateLecture={handleCreateLecture}
          onTempSave={handleTempSave}
          onSave={handleSave}
          onDelete={isEditMode ? handleDelete : undefined}
          saving={saving}
          isEditMode={isEditMode}
          hasLectureId={!!currentLectureId}
        />
      </PageContent>

        <LectureListModal
          isOpen={isLectureListModalOpen}
          onClose={handleCloseLectureList}
          lectureList={lectureList}
          selectedLectures={recommendedLectures}
          onLectureSelect={handleLectureSelect}
        />
    </PageContainer>
  );
};

export default LectureRegisterComponent;
